import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Hash,
  Smartphone,
  CheckCircle2,
  ShieldAlert,
  ArrowRight,
} from "lucide-react";

export default function TrackingWidget({ onSearch, isLoading = false }) {
  const [activeTab, setActiveTab] = useState("track");
  const [searchMode, setSearchMode] = useState("awb");
  const [inputValue, setInputValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // OTP Flow Simulation
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [tempMobile, setTempMobile] = useState("");

  // Shipping quick check state
  const [quickOrigin, setQuickOrigin] = useState("");
  const [quickDest, setQuickDest] = useState("");
  const [quickWeight, setQuickWeight] = useState("1");

  const handleTrackSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!inputValue.trim()) {
      setErrorMsg("Please enter a valid tracking key.");
      return;
    }

    if (searchMode === "mobile") {
      // Validate mobile looks like 10 digits
      const cleaned = inputValue.replace(/\D/g, "");
      if (cleaned.length < 10) {
        setErrorMsg("Please enter a valid 10-digit mobile number.");
        return;
      }
      setTempMobile(cleaned);
      setShowOtpScreen(true);
    } else {
      onSearch(searchMode, inputValue.trim());
    }
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    if (otpValue.trim() === "1234" || otpValue.trim() === "JPSY") {
      setShowOtpScreen(false);
      onSearch("mobile", tempMobile);
      setOtpValue("");
    } else {
      setErrorMsg('Invalid verification code. Enter "1234" to simulate.');
    }
  };

  const getPlaceholder = () => {
    switch (searchMode) {
      case "mobile":
        return "Enter your mobile number (e.g., 9876543210)";
      case "awb":
        return "Enter JPSY AWB (e.g., JPSY10012026)";
      case "orderId":
        return "Enter e-commerce Order ID (e.g., ORD-554219)";
      case "lrn":
        return "Enter Lorry Receipt Number LRN (e.g., LRN-998811)";
    }
  };

  return (
    <div
      id="jpsy-tracking-widget"
      className="bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden max-w-lg w-full"
    >
      {/* Top Selector Menu */}
      <div className="flex border-b border-slate-100 bg-slate-50/50">
        <button
          onClick={() => {
            setActiveTab("track");
            setShowOtpScreen(false);
            setErrorMsg("");
          }}
          className={`flex-1 py-4 px-6 text-center font-sans font-semibold text-sm transition-all relative ${
            activeTab === "track"
              ? "text-blue-600 bg-white"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Track order
          {activeTab === "track" && (
            <motion.div
              layoutId="activeTabUnderline"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
            />
          )}
        </button>
        <button
          onClick={() => {
            setActiveTab("ship");
            setErrorMsg("");
          }}
          className={`flex-1 py-4 px-6 text-center font-sans font-semibold text-sm transition-all relative ${
            activeTab === "ship"
              ? "text-blue-600 bg-white"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Quick Rate Estimate
          {activeTab === "ship" && (
            <motion.div
              layoutId="activeTabUnderline"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
            />
          )}
        </button>
      </div>

      <div className="p-6">
        {/* TRACK ORDER MODULE */}
        <AnimatePresence mode="wait">
          {activeTab === "track" && !showOtpScreen && (
            <motion.div
              key="track-screen"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-5"
            >
              <div>
                <h3 className="text-xl font-sans font-bold text-slate-900 tracking-tight">
                  Track your shipment
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Check progress on e-commerce packages, local lorry despatches,
                  and global air freight.
                </p>
              </div>

              {/* Sub-Tabs for Search Modes */}
              <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100 rounded-lg">
                {["mobile", "awb", "orderId", "lrn"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setSearchMode(mode);
                      setInputValue("");
                      setErrorMsg("");
                    }}
                    className={`py-2 px-1 text-[11px] font-semibold rounded-md transition-all text-center capitalize ${
                      searchMode === mode
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {mode === "orderId"
                      ? "Order ID"
                      : mode === "lrn"
                        ? "LRN"
                        : mode}
                  </button>
                ))}
              </div>

              {/* Tracking input form */}
              <form onSubmit={handleTrackSubmit} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    {searchMode === "mobile" ? (
                      <Smartphone className="w-5 h-5" />
                    ) : (
                      <Hash className="w-5 h-5" />
                    )}
                  </div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setErrorMsg("");
                    }}
                    placeholder={getPlaceholder()}
                    className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-slate-800 transition-all font-sans"
                  />
                </div>

                {errorMsg && (
                  <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 p-3 rounded-lg border border-rose-100">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-sm shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : searchMode === "mobile" ? (
                    "Get OTP & Track"
                  ) : (
                    "Track Shipment"
                  )}
                </button>
              </form>

              {/* Quick info notes */}
              <div className="border-t border-slate-100 pt-4 flex gap-3 text-[11px] text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 shrink-0 animate-ping" />
                <p>
                  Search with{" "}
                  <span className="font-semibold text-slate-600">
                    JPSY10012026
                  </span>{" "}
                  (Active) or mobile{" "}
                  <span className="font-semibold text-slate-600">
                    9876543210
                  </span>{" "}
                  to test live status.
                </p>
              </div>
            </motion.div>
          )}

          {/* OTP POPUP VERIFICATION SCREEN (For Mobile authentication simulation) */}
          {activeTab === "track" && showOtpScreen && (
            <motion.div
              key="otp-screen"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-5"
            >
              <div>
                <h3 className="text-xl font-sans font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                  Confirm OTP Verification
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  We sent a simulated 4-digit security code to your mobile{" "}
                  <span className="font-mono text-slate-800 font-semibold">
                    +91 {tempMobile}
                  </span>
                  .
                </p>
              </div>

              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3.5 text-xs text-blue-800 flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 shrink-0 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">
                    Sandbox Code Generated
                  </p>
                  <p className="text-blue-700/90 mt-0.5">
                    Please type code{" "}
                    <span className="font-bold bg-white px-2 py-0.5 rounded border border-blue-200">
                      1234
                    </span>{" "}
                    or{" "}
                    <span className="font-bold bg-white px-2 py-0.5 rounded border border-blue-200">
                      JPSY
                    </span>{" "}
                    to successfully login.
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Verification OTP Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otpValue}
                    onChange={(e) => {
                      setOtpValue(e.target.value);
                      setErrorMsg("");
                    }}
                    placeholder="Enter verification code"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-center font-mono text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-800"
                  />
                </div>

                {errorMsg && (
                  <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 p-3 rounded-lg border border-rose-100">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOtpScreen(false);
                      setErrorMsg("");
                    }}
                    className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold py-3 px-4 rounded-xl transition-colors text-sm"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-sm shadow-md"
                  >
                    Verify & Track
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* QUICK RATE ESTIMATE MODULE */}
          {activeTab === "ship" && (
            <motion.div
              key="ship-screen"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-4"
            >
              <div>
                <h3 className="text-xl font-sans font-bold text-slate-900 tracking-tight">
                  Calculate Rates & Delivery ETA
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Provide route pincodes to calculate linehaul and delivery
                  fares instantaneously.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Pickup Pincode
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={quickOrigin}
                    onChange={(e) =>
                      setQuickOrigin(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="e.g., 560100"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Delivery Pincode
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={quickDest}
                    onChange={(e) =>
                      setQuickDest(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="e.g., 400050"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={quickWeight}
                  onChange={(e) => setQuickWeight(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  // Trigger redirection to shipping calculator tab
                  const event = new CustomEvent("jpsy-navigate", {
                    detail: {
                      tab: "booking",
                      origin: quickOrigin,
                      dest: quickDest,
                      weight: quickWeight,
                    },
                  });
                  window.dispatchEvent(event);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-sm shadow-md flex items-center justify-center gap-1.5 mt-2 cursor-pointer"
              >
                <span>Calculate Shipping Cost</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
